import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { uploadAttachment } from "@/services/clientAreaService";
import { updateCotacaoField } from "@/services/flowQuote";
import { CotacaoEcommerce } from "@/types/Cotacao";
import { formatDateToYMD } from "@/utils/format";
import { addDays, format, isBefore, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertTriangle,
  CalendarIcon,
  ExternalLink,
  FileText,
  Upload,
} from "lucide-react";
import { DragEvent, useEffect, useRef, useState } from "react";
import { GoAlert } from "react-icons/go";

interface ContractDocumentsCardProps {
  cotacao: CotacaoEcommerce;
  onUpdate: (cve_codigo: string) => Promise<void>;
  simplified?: boolean;
}

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const ACCEPTED_EXT = ".png,.jpeg,.jpg,.pdf";

const SLOTS = [
  { sigla: "cv1", label: "CNH", urlKey: "cv1_url" as const },
  { sigla: "cv2", label: "Documento do Veículo", urlKey: "cv2_url" as const },
  {
    sigla: "cv3",
    label: "Comprovante de Residência",
    urlKey: "cv3_url" as const,
  },
];

interface SlotState {
  uploading: boolean;
  progress: number;
  error: string | null;
}

export function ContractDocumentsCard({
  cotacao,
  onUpdate,
  simplified = false,
}: ContractDocumentsCardProps) {
  const [slots, setSlots] = useState<SlotState[]>(
    SLOTS.map(() => ({ uploading: false, progress: 0, error: null })),
  );

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const setSlotState = (index: number, patch: Partial<SlotState>) => {
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  const validate = (f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type))
      return "Apenas PNG, JPEG ou PDF são permitidos.";
    if (f.size > MAX_SIZE) return "Tamanho máximo: 10 MB.";
    return null;
  };

  const handleUpload = async (index: number, file: File) => {
    const err = validate(file);
    if (err) {
      setSlotState(index, { error: err });
      return;
    }

    setSlotState(index, { error: null, uploading: true, progress: 50 });

    try {
      await uploadAttachment(SLOTS[index].sigla, cotacao.cve_codigo, file);
      setSlotState(index, { progress: 100 });
      await onUpdate(cotacao.cve_codigo);
    } catch {
      setSlotState(index, { error: "Erro ao fazer upload. Tente novamente." });
    } finally {
      setSlotState(index, { uploading: false, progress: 0 });
    }
  };

  const handleFileChange = (index: number, fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    handleUpload(index, fileList[0]);
  };

  const handleDrop = (index: number, e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    handleUpload(index, e.dataTransfer.files[0]);
  };

  const showBaseInfo = Number(cotacao.tro_codigo) === 1;

  const canEditDate = Number(cotacao.ccs_codigo) < 4;

  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (cotacao.cve_data_previsao_recebimento) {
      try {
        const parsed = parse(
          cotacao.cve_data_previsao_recebimento,
          "dd/MM/yyyy",
          new Date(),
        );
        setDate(parsed);
      } catch {
        setDate(undefined);
      }
    }
  }, [cotacao.cve_data_previsao_recebimento]);

  const minDate = addDays(new Date(), 1);
  const isDateDisabled = (d: Date) => {
    if (isBefore(d, minDate)) return true;
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  const handleDateChange = async (newDate: Date | undefined) => {
    if (!newDate) return;
    setIsUpdatingDate(true);
    setDateError(null);
    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_data_previsao_recebimento",
        formatDateToYMD(newDate),
      );
      await onUpdate(cotacao.cve_codigo);
      setDate(newDate);
    } catch {
      setDateError("Não foi possível atualizar a data. Tente novamente.");
    } finally {
      setIsUpdatingDate(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Upload de documentos */}
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-n-green">Documentos:</h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SLOTS.map(({ label, urlKey }, index) => {
            const existingUrl = cotacao[urlKey];
            const slot = slots[index];
            const fileName = existingUrl?.split("/").pop() ?? "";
            const fullUrl = existingUrl
              ? `${import.meta.env.VITE_API_URL}/${existingUrl}`
              : null;
            const isImage = /\.(png|jpe?g)$/i.test(existingUrl ?? "");

            return (
              <div key={urlKey} className="flex flex-col gap-2">
                <p className="text-sm font-medium text-n-green">{label}</p>

                {/* Card único de altura fixa — muda aparência conforme estado */}
                <div className="relative h-36 rounded-xl border-2 overflow-hidden transition-all flex flex-col border-gray-200 bg-white">
                  {existingUrl && fullUrl ? (
                    /* Estado: arquivo enviado */
                    <div className="flex flex-col h-full">
                      <a
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative flex flex-col items-center justify-center gap-2 flex-1 px-3 hover:bg-black/5 transition-colors overflow-hidden"
                      >
                        {isImage ? (
                          <>
                            <img
                              src={fullUrl}
                              alt={fileName}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-black/40">
                              <ExternalLink className="w-3.5 h-3.5 text-white" />
                            </div>
                          </>
                        ) : (
                          <>
                            <FileText className="w-8 h-8 text-n-green" />
                            <span className="text-xs text-gray-600 text-center line-clamp-2 leading-tight px-1">
                              {fileName}
                            </span>
                            <ExternalLink className="w-3 h-3 text-gray-400" />
                          </>
                        )}
                      </a>
                      <button
                        type="button"
                        disabled={slot.uploading}
                        onClick={() =>
                          !slot.uploading && inputRefs[index].current?.click()
                        }
                        className="relative z-10 w-full border-t border-gray-100 py-1.5 text-xs text-gray-500 bg-white hover:bg-gray-50 hover:text-n-green transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                      >
                        <Upload className="w-3 h-3" />
                        Substituir
                      </button>
                    </div>
                  ) : (
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(index, e)}
                      onClick={() =>
                        !slot.uploading && inputRefs[index].current?.click()
                      }
                      className={`flex flex-col items-center justify-center gap-1.5 h-full border-2 border-dashed rounded-[10px] m-px transition-colors ${
                        slot.uploading
                          ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                          : "border-gray-300 hover:border-n-green cursor-pointer hover:bg-gray-50"
                      }`}
                    >
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-n-text-neutral">
                        Arraste ou clique
                      </span>
                      <span className="text-xs text-gray-400">
                        PNG, JPEG, PDF · 10 MB
                      </span>
                    </div>
                  )}

                  <input
                    type="file"
                    accept={ACCEPTED_EXT}
                    ref={inputRefs[index]}
                    className="hidden"
                    onChange={(e) => handleFileChange(index, e.target.files)}
                  />

                  {slot.uploading && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                      <div
                        className="h-full bg-n-green transition-all"
                        style={{ width: `${slot.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {slot.error && (
                  <div className="flex items-center gap-1 text-red-600 text-xs">
                    <AlertTriangle className="w-3 h-3" />
                    {slot.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {!simplified && showBaseInfo && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-n-green">
              Base para Recebimento do Veículo:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-sm font-medium text-n-green">Base:</p>
                <p className="text-sm text-gray-700">
                  {cotacao.bop_origem_descricao || "—"}
                </p>
              </div>
              {cotacao.base_origem_endereco && (
                <div>
                  <p className="text-sm font-medium text-n-green">Endereço:</p>
                  <p className="text-sm text-gray-700">
                    {cotacao.base_origem_endereco}
                  </p>
                </div>
              )}
              {cotacao.base_origem_contato && (
                <div>
                  <p className="text-sm font-medium text-n-green">Contato:</p>
                  <p className="text-sm text-gray-700">
                    {cotacao.base_origem_contato}
                  </p>
                </div>
              )}
              {cotacao.base_origem_horario && (
                <div className="">
                  <p className="text-sm font-medium text-n-green">
                    Horário de funcionamento:
                  </p>
                  <p className="text-sm text-gray-700">
                    {cotacao.base_origem_horario}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!simplified && (
        <>
          <Separator />
          <div className="flex flex-col  gap-4">
            <h3 className="text-lg font-semibold text-n-green">
              Data prevista de recebimento:
            </h3>

            <div className="flex flex-col md:flex-row items-start gap-3 py-3 px-6 border border-n-green rounded-xl">
              <div className="flex items-start gap-4 flex-1">
                <div className="flex-shrink-0 mt-0.5">
                  <GoAlert className="text-n-green h-6 w-6" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-n-text leading-tight">
                    {canEditDate
                      ? "Confirme a data prevista para o recebimento do veículo."
                      : "Data prevista para o recebimento do veículo:"}
                  </p>
                  {canEditDate ? (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Altere quando precisar, sem custo.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Data definida anteriormente.
                    </p>
                  )}
                </div>
              </div>

              {canEditDate ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="input"
                      size="md"
                      disabled={isUpdatingDate}
                      className="h-[32px] w-full md:w-fit flex-shrink-0 md:justify-start rounded-full justify-center md:text-left text-center text-sm font-normal border-zinc-300 border bg-white hover:bg-gray-50"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? (
                        format(date, "dd/MM/yyyy", { locale: ptBR })
                      ) : (
                        <span>Selecione</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateChange}
                      disabled={isDateDisabled}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="h-[32px] w-full md:w-fit flex-shrink-0 flex items-center justify-center md:justify-start px-4 rounded-full text-sm font-normal border-zinc-300 border bg-gray-50">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? (
                    format(date, "dd/MM/yyyy", { locale: ptBR })
                  ) : (
                    <span>Não definida</span>
                  )}
                </div>
              )}
            </div>

            {dateError && <p className="text-sm text-red-600">{dateError}</p>}
          </div>
        </>
      )}
    </div>
  );
}
